import { useState, useRef, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Send, LifeBuoy } from "lucide-react";

// Shared DonkeyBot conversation (used by the /donkeybot page AND the floating launcher).
// Behaviour is identical to the original page: deterministic FAQ when logged out,
// AI when signed in + eligible, human escalation on confirm, graceful failure.
export default function DonkeyBotChat({ heightClass = "h-[52vh]" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi, I'm DonkeyBot — your guide to The Honest Donkey. Ask me about the stages, pricing or getting around. I can't change your reports, billing or account, and I'll point you to a human when needed." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalation, setEscalation] = useState(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const url = user ? "/donkeybot/ask" : "/donkeybot/faq";
      const { data } = await api.post(url, { message: msg, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "bot", text: data.answer, source: data.source, mode: data.mode, escalate: data.escalate_available }]);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      setMessages((m) => [...m, { role: "bot", text: detail || "DonkeyBot is unavailable right now. Please try the Support Centre or contact page." }]);
    } finally { setBusy(false); }
  };

  const startEscalation = async () => {
    if (!user) { toast.info("Please sign in to send this to support."); return; }
    if (!sessionId) { toast.info("Ask a question first, then I can summarise it for support."); return; }
    try { const { data } = await api.post("/donkeybot/escalate", { session_id: sessionId, confirm: false }); setEscalation(data.summary); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmEscalation = async () => {
    try { const { data } = await api.post("/donkeybot/escalate", { session_id: sessionId, confirm: true, subject: "DonkeyBot escalation" }); setEscalation(null); toast.success(`Sent to support (${data.reference}). We'll reply in your account.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className={`card-donkey rounded-2xl p-4 ${heightClass} overflow-y-auto space-y-3`} data-testid="donkeybot-messages" role="log" aria-label="DonkeyBot conversation" aria-live="polite">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-donkey-orange text-donkey-black" : "bg-donkey-bg border border-white/10 text-donkey-warm/90"}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.source && <p className="text-[10px] text-donkey-grey mt-1">Source: {m.source}{m.mode === "deterministic" ? " · instant answer" : m.mode === "ai" ? " · AI" : ""}</p>}
              {m.escalate && m.role === "bot" && <button onClick={startEscalation} className="text-[11px] text-donkey-orange hover:underline mt-1.5 flex items-center gap-1" data-testid="escalate-btn"><LifeBuoy className="w-3 h-3" /> Talk to a human</button>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-donkey-bg border border-white/10 rounded-2xl px-4 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-donkey-orange" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask DonkeyBot…" aria-label="Message DonkeyBot" maxLength={800} className="bg-donkey-bg border-white/15" data-testid="donkeybot-input" />
        <Button onClick={send} disabled={busy} aria-label="Send message" className="bg-donkey-orange text-donkey-black font-semibold" data-testid="donkeybot-send"><Send className="w-4 h-4" /></Button>
      </div>

      {escalation !== null && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] px-5" data-testid="escalation-modal">
          <div className="card-donkey rounded-2xl p-6 max-w-lg w-full">
            <h2 className="font-heading font-semibold text-lg mb-2">Send this to support?</h2>
            <p className="text-xs text-donkey-grey mb-3">Review the conversation summary below. It will only be sent to the Support Inbox if you confirm — nothing is sent automatically.</p>
            <pre className="text-xs text-donkey-warm/85 whitespace-pre-wrap bg-donkey-bg border border-white/10 rounded-lg p-3 max-h-52 overflow-y-auto" data-testid="escalation-summary">{escalation}</pre>
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="ghost" onClick={() => setEscalation(null)} className="border border-white/10" data-testid="escalation-cancel">Cancel</Button>
              <Button onClick={confirmEscalation} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="escalation-confirm">Confirm & send</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
